import { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Formik } from 'formik'

import { ECreateScanMode, EScanRecordType, IScanCreateForm } from 'src/types'
import { scanCreateSchema } from 'src/common/validations'
import { useAddScan, useScanCreateInitValues } from 'src/hooks'
import { RelationContext } from './relations-context.component'
import { joinAbsRoute } from 'src/common/utils'
import { ROUTES } from 'src/constants'
import { Loader } from 'src/components/loader'

interface IProps {
  children: ReactNode
  camera?: boolean
}

export const FormWrapper = ({ children, camera }: IProps) => {
  const [formType, setFormType] = useState(EScanRecordType.General)
  const [createScanMode, setCreateScanMode] = useState(ECreateScanMode.Default)

  const [searchParams] = useSearchParams()
  const { accountId } = useParams()
  const navigate = useNavigate()

  const { mutateAsync: addScan } = useAddScan()
  const { data: init, isLoading: isInitialLoading } = useScanCreateInitValues({
    productId: searchParams.get('productId') || undefined,
    draftId: searchParams.get('draftId') || undefined,
    accountId: accountId,
  })

  useEffect(() => {
    if (!init || !init.record_type) return

    setFormType(init.record_type)
  }, [init])

  const handleSubmit = async (values: IScanCreateForm) => {
    const resp = await addScan({
      ...values,
      record_type: formType,
      scan_mode: createScanMode,
    })
    if (!resp) return

    navigate(
      accountId
        ? joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.SCAN_HISTORY)
        : joinAbsRoute(ROUTES.SCAN_HISTORY),
      { state: { from: 'create-scan' } },
    )
  }

  if (isInitialLoading) {
    return <Loader />
  }

  return (
    <Formik
      validationSchema={scanCreateSchema({
        withImpact: formType === EScanRecordType.Impact,
        withMovement: formType === EScanRecordType.Movement,
        scanMode: createScanMode,
      })}
      onSubmit={handleSubmit}
      initialValues={{
        scanner_latitude: init?.scanner_latitude || 0,
        scanner_longitude: init?.scanner_longitude || 0,
        batch_id: init?.batch_id || '',

        sender_reference_id: init?.sender_reference_id || '',
        receiver_reference_id: init?.receiver_reference_id || '',
        invoice_reference: init?.invoice_reference || '',

        impact_id: init?.impact?.id || null,
        impact_quantity: init?.impact_quantity || null,

        note: init?.note || '',
        media: init?.media || [],

        step_id: init?.step?.id || '',
        account_id: init?.account?.id || '',
        product_id: init?.product?.id || '',
      }}
    >
      <RelationContext
        camera={camera}
        step={init?.step || null}
        account={init?.account || null}
        product={init?.product || null}
        formType={formType}
        setFormType={setFormType}
        createScanMode={createScanMode}
        setCreateScanMode={setCreateScanMode}
      >
        {children}
      </RelationContext>
    </Formik>
  )
}
